import { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { BudgetRow, VendorCategory, Wedding } from '@guabi/shared';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { Metric, MetricFootnote } from '@/components/ui/Stat';
import { TextInput } from '@/components/ui/Field';
import { money } from '@/lib/format';
import { Scenarios } from './Scenarios';
import { useBudget, useBudgetNarrative, useSetPlanned } from './queries';

/**
 * Planned against what is actually committed.
 *
 * Nothing on this screen is stored except the planned figures. Everything else
 * is derived from the suppliers and the booked venue on each read, so the budget
 * and the suppliers tab cannot disagree.
 *
 * The number that leads is **what is left**, and it is allowed to be negative —
 * going over is the thing worth seeing, and a figure floored at zero would hide
 * exactly that.
 */
export function BudgetTab() {
  const { t } = useTranslation();
  const { wedding } = useOutletContext<{ wedding: Wedding; canWrite: boolean }>();
  const wedId = wedding.wedId;
  const currency = wedding.settings.currency;

  const budget = useBudget(wedId);
  const rollup = budget.data;
  const over = (rollup?.totals.remainingCents ?? 0) < 0;

  return (
    <section className="flex flex-col gap-6">
      <div>
        <p className="eyebrow">{t('nav.budget')}</p>
        <h1 className="font-display text-display">{t('budget.heading')}</h1>
      </div>

      {budget.isLoading && <p className="text-body text-leaf-soft">{t('common.loading')}</p>}
      {budget.isError && <p className="text-body text-leaf-soft">{t('common.somethingWrong')}</p>}

      {rollup && (
        <>
          <div className="panel flex flex-col gap-5 p-5">
            <div className="flex flex-wrap gap-6">
              <Metric
                label={t('budget.planned')}
                value={money(rollup.totals.plannedCents, currency)}
                sub={t('budget.plannedSub')}
              />
              <Metric
                label={over ? t('budget.overBy') : t('budget.left')}
                value={money(Math.abs(rollup.totals.remainingCents), currency)}
                sub={over ? t('budget.overSub') : t('budget.leftSub')}
              />
            </div>
            <div className="border-t border-line pt-4">
              <MetricFootnote
                items={[
                  t('budget.committed', {
                    amount: money(rollup.totals.actualCents, currency),
                  }),
                  t('budget.paidSoFar', { amount: money(rollup.totals.paidCents, currency) }),
                  rollup.upcomingPaymentsCents > 0
                    ? t('budget.dueSoon', {
                        amount: money(rollup.upcomingPaymentsCents, currency),
                      })
                    : undefined,
                ]}
              />
            </div>
          </div>

          {/* The venue is usually the largest line, and its payments cannot be
              ticked off until it exists as a supplier too. */}
          {rollup.venueUntracked && (
            <p className="text-caption text-leaf-soft" role="note">
              {t('budget.venueUntracked')}
            </p>
          )}

          {rollup.rows.length === 0 ? (
            <EmptyState title={t('budget.emptyTitle')} body={t('budget.emptyBody')} />
          ) : (
            <div className="panel overflow-x-auto">
              {/*
                Planned and remaining are the pair this table exists to compare,
                so they keep their columns at every width. Actual and paid move
                under the category name below `md` rather than pushing the
                comparison off the side of the screen.
              */}
              <table className="w-full border-collapse text-body">
                <thead>
                  <tr className="border-b border-line text-left">
                    <th className="px-3 py-3 text-caption font-medium sm:px-4">
                      {t('budget.category')}
                    </th>
                    <th className="px-3 py-3 text-caption font-medium sm:px-4">
                      {t('budget.plannedCol')}
                    </th>
                    <th className="hidden px-4 py-3 text-caption font-medium md:table-cell">
                      {t('budget.actual')}
                    </th>
                    <th className="hidden px-4 py-3 text-caption font-medium md:table-cell">
                      {t('budget.paid')}
                    </th>
                    <th className="px-3 py-3 text-caption font-medium sm:px-4">
                      {t('budget.remaining')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rollup.rows.map((row) => (
                    <BudgetTableRow
                      key={row.category}
                      wedId={wedId}
                      row={row}
                      currency={currency}
                    />
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-line font-medium">
                    <td className="px-3 py-3 sm:px-4">{t('budget.total')}</td>
                    <td className="tabular px-3 py-3 sm:px-4">
                      {money(rollup.totals.plannedCents, currency)}
                    </td>
                    <td className="tabular hidden px-4 py-3 md:table-cell">
                      {money(rollup.totals.actualCents, currency)}
                    </td>
                    <td className="tabular hidden px-4 py-3 md:table-cell">
                      {money(rollup.totals.paidCents, currency)}
                    </td>
                    <td
                      className={`tabular px-3 py-3 sm:px-4 ${over ? 'text-status-declinedFg' : ''}`}
                    >
                      {money(rollup.totals.remainingCents, currency)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          <BudgetNarrative wedId={wedId} />

          <Scenarios wedId={wedId} currency={currency} />
        </>
      )}
    </section>
  );
}

/**
 * One category, with the planned figure editable in place.
 *
 * Saved on blur rather than on every keystroke: it is a number somebody types in
 * one go, and a request per digit would be both wasteful and jumpy.
 */
function BudgetTableRow({
  wedId,
  row,
  currency,
}: {
  wedId: string;
  row: BudgetRow;
  currency: string;
}) {
  const { t } = useTranslation();
  const setPlanned = useSetPlanned(wedId);
  const [draft, setDraft] = useState(String(row.plannedCents / 100));
  const [editing, setEditing] = useState(false);

  const commit = () => {
    setEditing(false);
    const cents = Math.round(Number(draft.replace(/[^0-9.]/g, '')) * 100);
    if (!Number.isFinite(cents) || cents === row.plannedCents) return;
    setPlanned.mutate({ category: row.category as VendorCategory, plannedCents: cents });
  };

  const over = row.remainingCents < 0;

  return (
    <tr className="border-b border-line last:border-0">
      <td className="px-3 py-3 sm:px-4">
        <span className="flex flex-col">
          <span>{t(`vendorCategories.${row.category}`)}</span>
          {/* The two columns that leave the grid below `md`. */}
          <span className="tabular mt-1 flex flex-wrap gap-x-2 text-caption text-leaf-soft md:hidden">
            <span>{t('budget.actualShort', { amount: money(row.actualCents, currency) })}</span>
            <span>{t('budget.paidShort', { amount: money(row.paidCents, currency) })}</span>
          </span>
        </span>
      </td>
      <td className="px-3 py-2 sm:px-4">
        {editing ? (
          <TextInput
            autoFocus
            inputMode="decimal"
            className="w-28"
            aria-label={t('budget.plannedFor', {
              category: t(`vendorCategories.${row.category}`),
            })}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commit();
              if (e.key === 'Escape') {
                setDraft(String(row.plannedCents / 100));
                setEditing(false);
              }
            }}
          />
        ) : (
          <button
            type="button"
            onClick={() => {
              setDraft(String(row.plannedCents / 100));
              setEditing(true);
            }}
            className="tabular min-h-tap rounded px-2 text-left underline decoration-line underline-offset-4 hover:bg-cream"
          >
            {money(row.plannedCents, currency)}
          </button>
        )}
      </td>
      <td className="tabular hidden px-4 py-3 text-caption text-leaf-soft md:table-cell">
        {money(row.actualCents, currency)}
      </td>
      <td className="tabular hidden px-4 py-3 text-caption text-leaf-soft md:table-cell">
        {money(row.paidCents, currency)}
      </td>
      <td className={`tabular px-3 py-3 sm:px-4 ${over ? 'text-status-declinedFg' : ''}`}>
        {money(row.remainingCents, currency)}
      </td>
    </tr>
  );
}

/**
 * The numbers above, in a few sentences, when asked for.
 *
 * A button rather than something that runs on load: every call costs the
 * couple's monthly allowance, and a paragraph nobody asked for is a paragraph
 * nobody reads. The table is computed and stays correct whether this works or
 * not, which is what the failure message says.
 */
function BudgetNarrative({ wedId }: { wedId: string }) {
  const { t } = useTranslation();
  const narrate = useBudgetNarrative(wedId);

  return (
    <section className="flex flex-col gap-3 border-t border-line pt-6">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-section">{t('budget.narrativeTitle')}</h2>
        <Button variant="secondary" loading={narrate.isPending} onClick={() => narrate.mutate()}>
          {narrate.data ? t('budget.narrativeAgain') : t('budget.narrativeAsk')}
        </Button>
      </div>
      {narrate.data && (
        <p className="max-w-prose whitespace-pre-line text-body leading-relaxed">
          {narrate.data.text}
        </p>
      )}
      {narrate.isError && (
        <p role="alert" className="text-caption text-status-declinedFg">
          {narrate.error.message}
        </p>
      )}
      {!narrate.data && !narrate.isError && (
        <p className="text-caption text-leaf-soft">{t('budget.narrativeHint')}</p>
      )}
    </section>
  );
}
